"use client";

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { request } from "@/lib/api/client";

export type ErrorReportStatus = "new" | "triaged" | "resolved";

export interface ErrorReport {
  id: string;
  createdAt: string;
  app: string;
  message: string;
  stack: string | null;
  statusCode: number | null;
  url: string;
  userAgent: string;
  appVersion: string | null;
  companyId: string | null;
  userId: string | null;
  context: unknown;
  status: ErrorReportStatus;
}

export interface ErrorReportFilters {
  page?: number;
  limit?: number;
  status?: ErrorReportStatus;
  app?: string;
  search?: string;
}

interface ListResponse {
  data: {
    items: ErrorReport[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  };
}

const buildQuery = (f: ErrorReportFilters): string => {
  const p = new URLSearchParams();
  if (f.page) p.set("page", String(f.page));
  if (f.limit) p.set("limit", String(f.limit));
  if (f.status) p.set("status", f.status);
  if (f.app) p.set("app", f.app);
  if (f.search) p.set("search", f.search);
  const q = p.toString();
  return q ? `?${q}` : "";
};

export const useErrorReports = (filters: ErrorReportFilters = {}) =>
  useQuery({
    queryKey: ["platform", "error-reports", filters],
    queryFn: () =>
      request<ListResponse>(`/platform/error-reports${buildQuery(filters)}`),
    placeholderData: keepPreviousData,
  });

export const useUpdateErrorReportStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ErrorReportStatus }) =>
      request(`/platform/error-reports/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["platform", "error-reports"] }),
  });
};
